import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui";
import { badgeVariantFromConfig } from "@/lib/statusBadgeFromConfig";
import { cn } from "@/lib/utils";
import type { BookingLifecycleStatus, HandshakeSessionStatus } from "@/types";
import {
  BOOKING_STATUS_CONFIG,
  PARTNER_DISPLAY_STATUS_CONFIG,
  bookingStatusConfig,
  partnerBookingStatusConfig,
} from "./bookingStatus";
import { partnerDisplayStatus } from "./sellerBookingRules";

interface BookingStatusBadgeProps {
  status: BookingLifecycleStatus;
  /** Linked handshake status — required so partner desync cannot silently omit it. */
  handshakeStatus: HandshakeSessionStatus | null;
  /** Use simplified pending/confirmed labels for activity owners. */
  partnerView?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function BookingStatusBadge({
  status,
  handshakeStatus,
  partnerView = false,
  size = "md",
  className,
}: BookingStatusBadgeProps) {
  const { t } = useTranslation("bookings");

  if (partnerView) {
    const displayStatus = partnerDisplayStatus(status, handshakeStatus);
    const config = partnerBookingStatusConfig(status, handshakeStatus);
    const Icon = config.icon;
    return (
      <Badge
        variant={badgeVariantFromConfig(
          displayStatus,
          PARTNER_DISPLAY_STATUS_CONFIG,
        )}
        size={size}
        className={cn(
          config.badgeRingClass && "ring-1 ring-inset",
          config.badgeRingClass,
          !config.emphasizeBadge &&
            "bg-surface-100 font-normal text-surface-600 [&_svg]:opacity-70",
          className,
        )}
      >
        <Icon aria-hidden />
        {t(config.labelKey)}
      </Badge>
    );
  }

  const config = bookingStatusConfig(status);
  const Icon = config.icon;
  return (
    <Badge variant={badgeVariantFromConfig(status, BOOKING_STATUS_CONFIG)} size={size} className={className}>
      <Icon aria-hidden />
      {t(config.labelKey)}
    </Badge>
  );
}
